import apiClient from "./client";
import type { PlanType } from "@/stores/planSelectionStore";

type ApiEnvelope<T> = {
  data?: T;
  message_key?: string;
};

/** Discount echoed back by POST /payments/create when a voucher was applied (JUB-27). */
export type PaymentIntentDiscount = {
  code: string;
  originalAmount: number;
  discountValue: number;
  unit: "percent" | "fixed" | string;
  discountAmount: number;
  finalAmount: number;
};

export type PaymentIntentPayload = {
  clientSecret: string | null;
  paymentIntentId: string;
  paymentId: number;
  amount: number;
  currency: string;
  plan: PlanType;
  /** JUB-157: the event this payment is bound to (always set for paid plans). */
  eventUuid?: string;
  testMode?: boolean;
  /** JUB-223: true when a 100% discount voucher reduced the amount to 0 RON and fulfilled immediately. */
  freeViaVoucher?: boolean;
  confirmed?: boolean;
  status?: string;
  discount?: PaymentIntentDiscount | null;
};

export type ConfirmPaymentPayload = {
  success: boolean;
  paymentId: number;
  plan: PlanType;
};

export type BuyerBillingDetails = {
  name: string;
  county: string;
  city: string;
  address: string;
  cui?: string;
  companyName?: string;
};

export type PaymentLegalAndBillingOptions = {
  termsAccepted?: boolean;
  withdrawalWaiverAccepted?: boolean;
  billing?: BuyerBillingDetails;
};

const unwrap = <T>(payload: T | ApiEnvelope<T>): T => {
  if (payload && typeof payload === "object" && "data" in payload && payload.data) {
    return payload.data;
  }
  return payload as T;
};

export async function createPlanPaymentIntent(
  plan: PlanType,
  eventUuid?: string,
  voucherCode?: string,
  options?: PaymentLegalAndBillingOptions,
): Promise<PaymentIntentPayload> {
  const response = await apiClient.post<ApiEnvelope<PaymentIntentPayload> | PaymentIntentPayload>("/payments/create", {
    plan,
    eventUuid,
    voucherCode,
    ...(options?.termsAccepted !== undefined ? { termsAccepted: options.termsAccepted } : {}),
    ...(options?.withdrawalWaiverAccepted !== undefined
      ? { withdrawalWaiverAccepted: options.withdrawalWaiverAccepted }
      : {}),
    ...(options?.billing ? { billing: options.billing } : {}),
  });
  return unwrap(response.data);
}

export async function confirmPlanPayment(
  paymentId: number,
  paymentIntentId: string,
  options?: PaymentLegalAndBillingOptions,
): Promise<ConfirmPaymentPayload> {
  const response = await apiClient.post<ApiEnvelope<ConfirmPaymentPayload> | ConfirmPaymentPayload>(
    "/payments/confirm",
    {
      paymentId,
      paymentIntentId,
      ...(options?.termsAccepted !== undefined ? { termsAccepted: options.termsAccepted } : {}),
      ...(options?.withdrawalWaiverAccepted !== undefined
        ? { withdrawalWaiverAccepted: options.withdrawalWaiverAccepted }
        : {}),
      ...(options?.billing ? { billing: options.billing } : {}),
    },
  );
  return unwrap(response.data);
}

export async function completePlanPaymentTest(
  paymentId: number,
  paymentIntentId: string,
  paymentMethod?: string,
  options?: PaymentLegalAndBillingOptions,
): Promise<ConfirmPaymentPayload> {
  const response = await apiClient.post<ApiEnvelope<ConfirmPaymentPayload> | PaymentIntentPayload>(
    "/payments/test-complete",
    {
      paymentId,
      paymentIntentId,
      paymentMethod,
      ...(options?.termsAccepted !== undefined ? { termsAccepted: options.termsAccepted } : {}),
      ...(options?.withdrawalWaiverAccepted !== undefined
        ? { withdrawalWaiverAccepted: options.withdrawalWaiverAccepted }
        : {}),
      ...(options?.billing ? { billing: options.billing } : {}),
    },
  );

  return unwrap(response.data as ApiEnvelope<ConfirmPaymentPayload> | ConfirmPaymentPayload);
}

